import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link as RouterLink, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, publicApi } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { computeLandingUrl } from "@/lib/trackingUrl";
import { Copy, MessageCircle, Facebook, Sparkles, ArrowRight, ShoppingBag, LogIn, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function fmtMoney(amount, currency = "GYD") {
  const n = Number(amount);
  if (!isFinite(n)) return "—";
  return `${currency} ${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function SharePage() {
  const [sp] = useSearchParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [intent, setIntent] = useState(null);
  const [error, setError] = useState(null);

  // Extract product context from URL — keep it intact through auth flow
  const ctx = useMemo(() => {
    const rawImage = sp.get("image") || "";
    const destRaw = sp.get("dest") || "";
    // If the shop passed a relative image URL (e.g. "/api/files/…"), resolve
    // it against the destination's origin so the browser fetches it from the
    // shop, not from trackmatrix.net.
    let image = rawImage;
    if (rawImage && !/^https?:\/\//i.test(rawImage)) {
      try {
        if (destRaw) {
          const destUrl = new URL(destRaw);
          image = new URL(rawImage, `${destUrl.protocol}//${destUrl.host}`).toString();
        }
      } catch {
        // leave image as-is if dest is unparseable
      }
    }
    return {
      dest: destRaw,
      product_id: sp.get("product_id") || "",
      title: sp.get("title") || "",
      price: sp.get("price") ? Number(sp.get("price")) : null,
      currency: sp.get("currency") || "GYD",
      commission_pct: sp.get("commission_pct") ? Number(sp.get("commission_pct")) : null,
      seller: sp.get("seller") || "",
      image,
      source: sp.get("source") || "",
    };
  }, [sp]);

  // Once logged in, ask the backend for (or create) the tracking link + intent
  useEffect(() => {
    if (loading || !user || !ctx.dest) return;
    (async () => {
      try {
        const { data } = await api.post("/share/intent", ctx);
        setIntent(data);
      } catch (e) {
        setError(e.response?.data?.detail || "Couldn't generate your share link");
      }
    })();
  }, [loading, user, ctx]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#9CA3AF]">Loading…</div>;

  if (!ctx.dest) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <ShoppingBag size={36} className="mx-auto text-[#9CA3AF] mb-3" />
          <div className="font-display font-black text-2xl text-[#111827]">Nothing to share here</div>
          <p className="text-sm text-[#4B5563] mt-2">
            This page needs a product destination. From the e-commerce site, click <span className="font-bold">"Share to earn"</span> on any product to come back here.
          </p>
          <RouterLink to="/dashboard" className="btn-primary inline-flex items-center gap-2 mt-6">
            Go to dashboard <ArrowRight size={14} />
          </RouterLink>
        </div>
      </div>
    );
  }

  if (!user) {
    // Preserve the full query string so we land back on /share after login
    const nextUrl = `/share?${sp.toString()}`;
    return <LoginGate ctx={ctx} nextUrl={nextUrl} />;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="border-b border-[#E5E7EB] bg-white">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-black tracking-tight">
            <span className="text-[#002FA7]">trackmatrix</span>
          </div>
          <RouterLink to="/dashboard" className="text-xs font-bold text-[#4B5563] hover:text-[#111827]">
            Dashboard →
          </RouterLink>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="label-eyebrow text-[#002FA7] flex items-center gap-2 mb-2">
          <Sparkles size={12} /> Share to earn
          {ctx.source && <span className="text-[#4B5563] normal-case tracking-normal">· from {ctx.source}</span>}
        </div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-black text-[#111827] mb-1">
          Share this and pocket the commission
        </h1>
        <p className="text-sm text-[#4B5563] mb-8">
          Every purchase made through your link credits your trackmatrix balance instantly.
        </p>

        {/* Product preview */}
        <ProductCard ctx={ctx} intent={intent} />

        {error && (
          <div className="mt-6 rounded-md bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B] text-sm p-3" data-testid="share-error">{error}</div>
        )}

        {intent && <ShareControls ctx={ctx} intent={intent} />}
      </main>
    </div>
  );
}

function ProductCard({ ctx, intent }) {
  const [imageOk, setImageOk] = useState(true);
  const youEarn = useMemo(() => {
    if (!ctx.price || !ctx.commission_pct) return null;
    return ctx.price * ctx.commission_pct / 100;
  }, [ctx]);
  return (
    <div className="card-flat p-6 flex flex-col sm:flex-row gap-6" data-testid="share-product-card">
      {ctx.image && imageOk ? (
        <img
          src={ctx.image}
          alt={ctx.title}
          onError={() => setImageOk(false)}
          referrerPolicy="no-referrer"
          className="w-full sm:w-44 h-44 rounded-md object-cover bg-[#F3F4F6] flex-shrink-0"
        />
      ) : (
        <div className="w-full sm:w-44 h-44 rounded-md bg-[#F3F4F6] flex items-center justify-center text-[#9CA3AF] flex-shrink-0" data-testid="share-product-image-fallback">
          <ShoppingBag size={36} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        {ctx.seller && <div className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] mb-1">{ctx.seller}</div>}
        <div className="font-display font-black text-xl text-[#111827] mb-3 line-clamp-3">{ctx.title || "Product"}</div>
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
          {ctx.price !== null && (
            <div>
              <div className="text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF]">Price</div>
              <div className="font-display font-black text-2xl text-[#111827]">{fmtMoney(ctx.price, ctx.currency)}</div>
            </div>
          )}
          {ctx.commission_pct !== null && (
            <div>
              <div className="text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF]">Commission</div>
              <div className="font-display font-black text-2xl text-[#002FA7]">{ctx.commission_pct}%</div>
            </div>
          )}
          {youEarn !== null && (
            <div>
              <div className="text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF]">You earn ~</div>
              <div className="font-display font-black text-2xl text-[#166534]">{fmtMoney(youEarn, ctx.currency)}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ShareControls({ ctx, intent }) {
  // Prefer the shareable_url (goes through /api/p/<slug> so WhatsApp/Facebook
  // get a rich preview card with the product image as og:image). Fall back
  // to the bare tracking_url for older backends that didn't return it.
  const trackingUrl = intent.shareable_url || intent.tracking_url;
  const defaultMsg = `${ctx.title ? ctx.title + " — " : ""}Check this out on ${ctx.source || "guyanadiscounts.com"}: ${trackingUrl}`;
  const [msg, setMsg] = useState(defaultMsg);

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(msg)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(trackingUrl)}`;

  // Compute the exact URL the shopper will land on after our redirector
  // rewrites the path and injects ?ref. This is the same helper /r/<slug>
  // uses, so what the affiliate sees here IS what the shopper will see.
  const landingUrl = useMemo(() => {
    // Mirror the backend's _final_destination(): start from the live dest
    // URL the user is sharing right now. Path correctness is what matters
    // since ?ref + ?p will be appended by computeLandingUrl().
    return computeLandingUrl({
      finalUrl: ctx.dest,
      slug: intent.link?.slug,
      productId: ctx.product_id || null,
    });
  }, [ctx.dest, ctx.product_id, intent.link]);

  return (
    <>
      <div className="card-flat p-6 mt-6">
        <div className="label-eyebrow text-[#4B5563] mb-2">Your tracking link</div>
        <div className="flex items-center gap-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-md px-3 py-2 font-mono text-sm">
          <span className="flex-1 truncate select-all" data-testid="share-tracking-url">{trackingUrl}</span>
          <button
            onClick={() => copyToClipboard(trackingUrl, "Link copied")}
            data-testid="share-copy-link"
            className="text-[#4B5563] hover:text-[#002FA7] p-1.5 rounded"
          >
            <Copy size={14} />
          </button>
        </div>
      </div>

      {landingUrl && (
        <div className="card-flat p-6 mt-6" data-testid="share-landing-preview-card">
          <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
            <div className="label-eyebrow text-[#4B5563] flex items-center gap-2">
              <CheckCircle2 size={12} className="text-[#10B981]" /> Where your shopper will land
            </div>
            <a
              href={landingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-landing-preview-open"
              className="text-xs font-bold text-[#002FA7] inline-flex items-center gap-1 hover:underline"
            >
              Preview <ExternalLink size={11} />
            </a>
          </div>
          <div className="flex items-start gap-2 bg-[#ECFDF5] border border-[#A7F3D0] rounded-md px-3 py-2 font-mono text-xs text-[#065F46] break-all" data-testid="share-landing-preview-url">
            {landingUrl}
          </div>
          <p className="text-xs text-[#4B5563] mt-2 leading-relaxed">
            Click <span className="font-bold">Preview</span> to open the destination yourself and confirm it's the right product before posting. The <span className="font-mono">?ref=</span> tag is what credits your commission.
          </p>
        </div>
      )}

      <div className="card-flat p-6 mt-6">
        <div className="label-eyebrow text-[#4B5563] mb-2">Message</div>
        <textarea
          data-testid="share-message-input"
          rows={3}
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          className="input-base font-mono text-xs leading-relaxed"
        />
        <p className="text-xs text-[#9CA3AF] mt-2">Edit the message before sharing on WhatsApp. Facebook shows its own preview.</p>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-6">
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-btn-whatsapp"
          className="inline-flex flex-col items-center justify-center gap-1 rounded-xl bg-[#25D366] hover:bg-[#1DA851] text-white font-bold py-5 transition-colors"
        >
          <MessageCircle size={22} />
          WhatsApp
        </a>
        <a
          href={facebookUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-btn-facebook"
          className="inline-flex flex-col items-center justify-center gap-1 rounded-xl bg-[#1877F2] hover:bg-[#0E5FD1] text-white font-bold py-5 transition-colors"
        >
          <Facebook size={22} />
          Facebook
        </a>
        <button
          onClick={() => copyToClipboard(trackingUrl, "Link copied — paste it anywhere")}
          data-testid="share-btn-copy"
          className="inline-flex flex-col items-center justify-center gap-1 rounded-xl bg-[#111827] hover:bg-[#000] text-white font-bold py-5 transition-colors"
        >
          <Copy size={22} />
          Copy
        </button>
      </div>
    </>
  );
}

function LoginGate({ ctx, nextUrl }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-[#FAFAFA]">
      <div className="max-w-md w-full card-flat p-8 text-center">
        <div className="h-12 w-12 rounded-full bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center mx-auto mb-4">
          <LogIn size={20} />
        </div>
        <div className="font-display font-black text-2xl text-[#111827]">Sign in to share</div>
        <p className="text-sm text-[#4B5563] mt-2">
          {ctx.title ? <>You're about to share <span className="font-bold text-[#111827]">"{ctx.title}"</span> and earn {ctx.commission_pct ? `${ctx.commission_pct}%` : "a"} commission on every sale.</> : "Sign in to generate your tracking link."}
        </p>
        <div className="flex flex-col gap-2 mt-6">
          <RouterLink
            to={`/login?next=${encodeURIComponent(nextUrl)}`}
            data-testid="share-login-link"
            className="btn-primary inline-flex items-center justify-center gap-2"
          >
            <LogIn size={14} /> Sign in
          </RouterLink>
          <RouterLink
            to={`/register?next=${encodeURIComponent(nextUrl)}`}
            data-testid="share-register-link"
            className="btn-secondary inline-flex items-center justify-center gap-2"
          >
            Create an account
          </RouterLink>
        </div>
      </div>
    </div>
  );
}
